import { useState, useRef, useCallback } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useScrollHeader } from '../../hooks/useScrollHeader';
import { useLocalizedPath } from '../../hooks/useLocalizedPath';
import { ShoppingBag } from 'lucide-react';
import { Button } from '../ui';
import { MenuIcon, GearsIcon, ShieldIcon, PuzzleIcon, WalletIcon } from '../icons';
import { MobileMenu } from './MobileMenu';
import { LanguageSwitcher } from './LanguageSwitcher';
import styles from './Header.module.css';

interface SubItem {
  label: string;
  description: string;
  hash: string;
  path?: string;
  external?: string;
}

interface NavItem {
  to: string;
  label: string;
  items?: SubItem[];
  overview?: string;
  menuOnly?: boolean;
}

const subIcons: Record<string, React.ReactNode> = {
  'buyback': <WalletIcon size={20} />,
  'reprise-parc': <WalletIcon size={20} />,
  'solutions': <PuzzleIcon size={20} />,
  'business-model': <GearsIcon size={20} />,
  'conformite': <ShieldIcon size={20} />,
  'shop-pro': <WalletIcon size={20} />,
  'shop-perso': <ShoppingBag size={20} strokeWidth={1.5} />,
};

const DARK_HERO_ROUTES = ['/', '/en', '/solutions', '/en/solutions', '/catalogue', '/en/catalog', '/buyback', '/en/buyback', '/reprise-parc-informatique', '/en/it-fleet-buyback', '/expertise', '/en/expertise', '/references', '/en/references', '/about', '/en/about', '/contact', '/en/contact', '/status', '/en/status', '/mentions-legales', '/en/legal-notice'];

export function Header() {
  const { t } = useTranslation('common');
  const { lp } = useLocalizedPath();
  const location = useLocation();
  const hasDarkHero =
    DARK_HERO_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith('/blog') ||
    location.pathname.startsWith('/en/blog');
  const isScrolled = useScrollHeader();
  const solidHeader = isScrolled || !hasDarkHero;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>(null);

  const solutionsMenuRaw = t('nav.solutionsMenu', { returnObjects: true });
  const solutionsMenu = Array.isArray(solutionsMenuRaw) ? solutionsMenuRaw as SubItem[] : [];

  const buyMenuRaw = t('nav.buyMenu', { returnObjects: true });
  const buyMenu = Array.isArray(buyMenuRaw) ? buyMenuRaw as SubItem[] : [];

  const navLinks: NavItem[] = [
    { to: lp('/solutions'), label: t('nav.solutions'), items: solutionsMenu, overview: t('nav.solutionsOverview') },
    { to: lp('/catalogue'), label: t('nav.catalogue') },
    { to: lp('/expertise'), label: t('nav.expertise') },
    { to: lp('/references'), label: t('nav.references') },
    { to: lp('/'), label: t('nav.buy'), items: buyMenu, overview: t('nav.buyOverview'), menuOnly: true },
  ];

  const openDropdown = useCallback((key: string) => {
    if (closeTimeout.current) clearTimeout(closeTimeout.current);
    setOpenKey(key);
  }, []);

  const closeDropdown = useCallback(() => {
    closeTimeout.current = setTimeout(() => setOpenKey(null), 150);
  }, []);

  return (
    <>
      <header className={`${styles.header} ${solidHeader ? styles.scrolled : ''}`}>
        <div className={styles.container}>
          <Link to={lp('/')} className={styles.logo}>
            <img
              src="/images/logos/logo-marque-noir.webp"
              alt="QBP.Solutions"
              width={160}
              height={62}
              className={styles.logoImage}
            />
          </Link>

          <nav className={styles.nav}>
            {navLinks.map((link) =>
              link.items ? (
                <div
                  key={link.to}
                  className={`${styles.navItem} ${styles.hasDropdown}`}
                  onMouseEnter={() => openDropdown(link.to)}
                  onMouseLeave={closeDropdown}
                  onFocus={() => openDropdown(link.to)}
                  onBlur={closeDropdown}
                >
                  {(() => {
                    const chevron = (
                      <svg
                        className={`${styles.chevron} ${openKey === link.to ? styles.chevronOpen : ''}`}
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    );
                    return link.menuOnly ? (
                      <span className={styles.navLink} role="button" tabIndex={0} aria-haspopup="true">
                        {link.label}
                        {chevron}
                      </span>
                    ) : (
                      <NavLink
                        to={link.to}
                        className={({ isActive }) =>
                          `${styles.navLink} ${isActive ? styles.active : ''}`
                        }
                      >
                        {link.label}
                        {chevron}
                      </NavLink>
                    );
                  })()}
                  <div className={`${styles.dropdown} ${openKey === link.to ? styles.dropdownOpen : ''}`}>
                    <div className={styles.dropdownMain}>
                      <span className={styles.dropdownMainLabel}>
                        {link.label}
                      </span>
                      <span className={styles.dropdownMainDesc}>
                        {link.overview}
                      </span>
                    </div>
                    <div className={styles.dropdownDivider} />
                    {link.items.map((sub) => {
                      const inner = (
                        <>
                          <span className={styles.dropdownIcon}>
                            {subIcons[sub.hash]}
                          </span>
                          <span className={styles.dropdownText}>
                            <span className={styles.dropdownLabel}>{sub.label}</span>
                            <span className={styles.dropdownDesc}>{sub.description}</span>
                          </span>
                        </>
                      );
                      return sub.external ? (
                        <a
                          key={sub.hash}
                          href={sub.external}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={styles.dropdownItem}
                          onClick={() => setOpenKey(null)}
                        >
                          {inner}
                        </a>
                      ) : (
                        <Link
                          key={sub.hash}
                          to={sub.path ? lp(sub.path) : `${link.to}#${sub.hash}`}
                          className={styles.dropdownItem}
                          onClick={() => setOpenKey(null)}
                        >
                          {inner}
                        </Link>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.active : ''}`
                  }
                >
                  {link.label}
                </NavLink>
              )
            )}
          </nav>

          <div className={styles.actions}>
            <LanguageSwitcher variant={solidHeader ? 'light' : 'dark'} />
            <Button href={lp('/contact')} className={styles.ctaButton} data-attr="cta-contact">
              {t('nav.cta')}
            </Button>
          </div>

          <button
            className={styles.menuButton}
            onClick={() => setIsMobileMenuOpen(true)}
            aria-label={t('aria.openMenu')}
          >
            <MenuIcon />
          </button>
        </div>
      </header>

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        links={navLinks}
      />
    </>
  );
}
